import { Suspense, lazy, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Route, Routes } from 'react-router';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';

import { DEFAULT_LOCALE } from '../configuration/lang/lang';
import { isUserSessionExpired } from '../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../configuration/setup/hooks';
import { getDisplayMessages, getLocale } from '../configuration/lang/langSlice';
import { DEFAULT_ROUTE } from '../routes/routes';
import DefaultRedirect from '../routes/DefaultRedirect';
import RouteUpdater from '../routes/RouteUpdater';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from './appSlice';
import AppHeader from '../features/header/AppHeader';
import SuspendedWithSpinner from '../components/SuspendedWithSpinner';
import { getSelectedAppLoginId } from '../features/appLogins/sidebarSlice';
import {
    hasAnalyticsAnalysisOverlayParam,
    removeAnalyticsAnalysisOverlayParam,
} from '../features/app/utils/analyticsAnalysisOverlayUtils';

import './App.css';
import { AppLoginsSidebar } from '../features/appLogins/AppLoginsSidebar';

// Lazy load pages for better performance and automatically split the bundle accordingly
const AppLogins = lazy(() => import('../pages/AppLoginPage'));
const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

const App = () => {
    const dispatch = useAppDispatch();
    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);
    const selectedAppLoginId = useAppSelector(getSelectedAppLoginId);
    const [showAnalyticsAnalysisOverlay] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return hasAnalyticsAnalysisOverlayParam();
    });

    useEffect(() => {
        if (!showAnalyticsAnalysisOverlay) {
            return;
        }

        removeAnalyticsAnalysisOverlayParam();
    }, [showAnalyticsAnalysisOverlay]);

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog());
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ApplicationLayout data-testid={'app-layout'}>
                <ApplicationLayout.Header>
                    <AppHeader />
                </ApplicationLayout.Header>
                {selectedAppLoginId && (
                    <ApplicationLayout.Sidebar className='right'>
                        <AppLoginsSidebar />
                    </ApplicationLayout.Sidebar>
                )}
                <ApplicationLayout.Body>
                    <NotificationsContainer />
                    <SessionExpiredDialog
                        locale={userLocale}
                        onClose={handleSessionExpiredDialogClose}
                        show={showSessionExpired}
                    />
                    <Routes>
                        <Route
                            path={DEFAULT_ROUTE}
                            element={
                                <SuspendedWithSpinner>
                                    <AppLogins />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route path='*' element={<DefaultRedirect />} />
                    </Routes>
                    <RouteUpdater />
                </ApplicationLayout.Body>
                {showAnalyticsAnalysisOverlay && (
                    <Suspense fallback={null}>
                        <AnalyticsAnalysisOverlay
                            bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                        />
                    </Suspense>
                )}
            </ApplicationLayout>
        </IntlProvider>
    );
};

export default App;
